/**
 * Medication administration (scheduled doses from the medication chart).
 * Exception doses (refused, not received, medication issue, needs review) are
 * individual records so the per-client / per-medicine charts and CSV exports
 * match the headline counts; routine buckets are monthly aggregates.
 */
import { Quarter, now, quarterOf, ymd } from '../../clock';
import { rng } from '../../rng';
import { Client, activeClients } from '../../world';
import { allocate, careStaff, dateIn, daysInMonth, historyMonths, inScope, memo, monthStart, round1, scopeOf, syncedAt, trendScopeOf } from './common';

export type Bucket = 'given' | 'self_administered' | 'refused' | 'not_received' | 'medication_issue' | 'unclassified' | 'not_required' | 'client_unavailable';

export const BUCKETS: Bucket[] = ['given', 'self_administered', 'refused', 'not_received', 'medication_issue', 'unclassified', 'not_required', 'client_unavailable'];

/** Full reason-code map (code → label, bucket). */
export const REASON_CODES: Record<string, { label: string; bucket: Bucket }> = {
    '1': { label: 'Refused', bucket: 'refused' },
    '2': { label: 'Vomiting', bucket: 'not_received' },
    '3': { label: 'Omitted', bucket: 'not_received' },
    '4': { label: 'Fasting', bucket: 'not_required' },
    '5': { label: 'Hospital', bucket: 'client_unavailable' },
    '6': { label: 'Absent', bucket: 'client_unavailable' },
    '7': { label: 'Not Required', bucket: 'not_required' },
    '8': { label: 'On Leave', bucket: 'client_unavailable' },
    '9': { label: 'Absconded', bucket: 'client_unavailable' },
    '10': { label: 'Oversight', bucket: 'not_received' },
    '11': { label: 'Other', bucket: 'unclassified' },
    '12': { label: 'Spat Out', bucket: 'not_received' },
    '13': { label: 'Dropped Medication', bucket: 'not_received' },
    '14': { label: 'No Medication To Administer', bucket: 'not_required' },
    '15': { label: 'Advised by GP not to administer', bucket: 'medication_issue' },
    '16': { label: 'Unexpected change in medication/s', bucket: 'medication_issue' },
    '17': { label: 'Replacement dosage administered', bucket: 'medication_issue' },
    '18': { label: 'Medication has been finished', bucket: 'medication_issue' },
    '19': { label: 'Prescription changed - not administered', bucket: 'medication_issue' },
    '20': { label: 'Client under other', bucket: 'client_unavailable' },
    '21': { label: 'Self-Administered', bucket: 'self_administered' },
    '22': { label: 'Part refused - complete incident report', bucket: 'refused' },
};

const CODE_WEIGHTS: Record<Exclude<Bucket, 'given'>, [string, number][]> = {
    self_administered: [['21', 1]],
    refused: [['1', 9], ['22', 1]],
    not_received: [['10', 35], ['3', 20], ['2', 15], ['12', 15], ['13', 15]],
    medication_issue: [['18', 35], ['15', 25], ['16', 15], ['19', 15], ['17', 10]],
    unclassified: [['11', 1]],
    not_required: [['7', 60], ['14', 30], ['4', 10]],
    client_unavailable: [['8', 35], ['5', 30], ['6', 25], ['20', 7], ['9', 3]],
};

/** Share of scheduled doses per non-given bucket. */
const RATES: Record<Exclude<Bucket, 'given'>, number> = {
    self_administered: 0.006,
    refused: 0.011,
    not_received: 0.0016,
    medication_issue: 0.0007,
    unclassified: 0.003,
    not_required: 0.011,
    client_unavailable: 0.018,
};

const RECORD_BUCKETS: Bucket[] = ['refused', 'not_received', 'medication_issue', 'unclassified'];

const MEDICINES: [string, string, string][] = [
    ['Metformin 500mg', '1', 'Oral'],
    ['Paracetamol 500mg', '2', 'Oral'],
    ['Amlodipine 5mg', '1', 'Oral'],
    ['Atorvastatin 20mg', '1', 'Oral'],
    ['Perindopril 4mg', '1', 'Oral'],
    ['Insulin glargine 100 units/mL', '18 units', 'Subcutaneous'],
    ['Movicol Sachet', '1', 'Oral'],
    ['Aspirin 100mg', '1', 'Oral'],
    ['Frusemide 40mg', '1', 'Oral'],
    ['Empagliflozin 10mg', '1', 'Oral'],
    ['Sodium valproate 200mg', '2', 'Oral'],
    ['Risperidone 1mg', '1', 'Oral'],
    ['Calcium carbonate 600mg', '1', 'Oral'],
    ['Vitamin D 1000IU', '1', 'Oral'],
    ['Salbutamol 100mcg inhaler', '2 puffs', 'Inhaled'],
];

const DUE_TIMES = ['07:30', '08:00', '12:00', '17:00', '20:00'];

export interface DoseException {
    client: string;
    program: string;
    site: string;
    medicine: string;
    dose: string;
    route: string;
    date: Date;
    due_time: string;
    code: string;
    bucket: Bucket;
    actioned_by: string;
}

interface MonthAgg {
    month: string;
    counts: Record<Bucket, number>;
    codes: Record<string, number>; // aggregate-bucket codes only
}

/** Clients on a medication chart: a stable subset of NDIS and aged care clients. */
export function medicationCohort(): Client[] {
    const pool = activeClients().filter(c => c.program === 'Disability Services (NDIS)' || c.program === 'Aged Care');
    return rng('medications:cohort').sample(pool, 42).sort((a, b) => a.id - b.id);
}

/**
 * How the exception rate (refused, not received, medication issue, needs
 * review) runs in a month, relative to the base RATES. A mild seasonal swing
 * (worse over the hot months, when clients travel for sorry business and
 * family, better through the cooler middle of the year) plus a per-quarter
 * drift (rostering, agency cover, a refresher on the medication chart), so
 * compliance moves between roughly 97.4% and 99.0% from quarter to quarter.
 * Seeded on its own stream so the per-dose draws are unaffected.
 */
const SEASONAL: Record<string, number> = {
    '01': 1.25, '02': 1.2, '03': 1.05, '04': 0.95, '05': 0.85, '06': 0.8,
    '07': 0.8, '08': 0.85, '09': 0.9, '10': 1.0, '11': 1.1, '12': 1.2,
};

function exceptionLevel(m: string): number {
    const q = quarterOf(monthStart(m)).key;
    const drift = rng(`medications:run:${q}`).around(1, 0.35, 0.65, 1.5, 3);
    const noise = rng(`medications:level:${m}`).around(1, 0.12, 0.8, 1.2, 3);
    return SEASONAL[m.slice(5)] * drift * noise;
}

function monthFraction(m: string): number {
    const n = now();
    const start = monthStart(m);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    if (n >= end) return 1;
    return Math.max(0.03, (n.getTime() - start.getTime()) / (end.getTime() - start.getTime()));
}

const history = memo(() => {
    const cohort = medicationCohort();
    const refusers = rng('medications:refusers').sample(cohort, 4);
    const n = now();
    const months: MonthAgg[] = [];
    const exceptions: DoseException[] = [];
    for (const m of historyMonths()) {
        const r = rng(`medications:${m}`);
        const days = daysInMonth(m);
        const frac = monthFraction(m);
        const fullTotal = Math.round(cohort.length * days * r.around(2.7, 0.08));
        const level = exceptionLevel(m);
        const counts = {} as Record<Bucket, number>;
        const codes: Record<string, number> = {};
        // Exception records for the whole month, then keep those that have happened.
        for (const bucket of RECORD_BUCKETS) {
            const k = bucket as Exclude<Bucket, 'given'>;
            // Refusals climb over summer (heat, travel for funerals and family business).
            const seasonal = bucket === 'refused' && ['12', '01', '02'].includes(m.slice(5)) ? 1.15 : 1;
            const expected = Math.max(0, Math.round(fullTotal * RATES[k] * level * seasonal * r.around(1, 0.25, 0.4, 1.8, 2)));
            let kept = 0;
            for (let i = 0; i < expected; i++) {
                const client = bucket === 'refused' && r.bool(0.45) ? r.pick(refusers) : r.pick(cohort);
                const [medicine, dose, route] = r.pick(MEDICINES);
                const date = dateIn(m, r.int(1, days), 8);
                const code = r.weighted(CODE_WEIGHTS[k]);
                if (date.getTime() > n.getTime()) {
                    continue;
                }
                kept++;
                exceptions.push({
                    client: client.code,
                    program: client.program,
                    site: client.site,
                    medicine,
                    dose,
                    route,
                    date,
                    due_time: r.pick(DUE_TIMES),
                    code,
                    bucket,
                    actioned_by: r.pick(careStaff(client.program, client.site)).name,
                });
            }
            counts[bucket] = kept;
        }
        // Routine buckets scale with how much of the month has elapsed.
        const total = Math.round(fullTotal * frac);
        for (const bucket of ['self_administered', 'not_required', 'client_unavailable'] as const) {
            counts[bucket] = Math.round(total * RATES[bucket] * r.around(1, 0.2, 0.5, 1.6, 2));
            const weights = CODE_WEIGHTS[bucket];
            allocate(counts[bucket], weights.map(w => w[1])).forEach((c, i) => {
                codes[weights[i][0]] = (codes[weights[i][0]] || 0) + c;
            });
        }
        const nonGiven = BUCKETS.filter(b => b !== 'given').reduce((s, b) => s + (counts[b] || 0), 0);
        counts.given = Math.max(0, total - nonGiven);
        months.push({ month: m, counts, codes });
    }
    return { months, exceptions };
});

function emptyBuckets(): Record<Bucket, number> {
    return Object.fromEntries(BUCKETS.map(b => [b, 0])) as Record<Bucket, number>;
}

function excluded(bucket: Bucket): boolean {
    return bucket === 'not_required' || bucket === 'client_unavailable';
}

function categoryOf(bucket: Bucket): string {
    switch (bucket) {
        case 'refused':
            return 'refused';
        case 'not_received':
        case 'medication_issue':
            return 'missed';
        case 'unclassified':
            return 'review';
        case 'given':
        case 'self_administered':
            return 'given';
        default:
            return 'excluded';
    }
}

function topBy<T>(items: T[], key: (t: T) => string, field: string, labelKey: string): Record<string, unknown>[] {
    const m = new Map<string, number>();
    items.forEach(i => m.set(key(i), (m.get(key(i)) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([k, c]) => ({ [labelKey]: k, [field]: c }));
}

/** One month's bucket counts, with the record buckets taken from the exceptions in `ex` (so a scope's start/end is honoured). */
function monthCounts(m: { month: string; counts: Record<Bucket, number> }, ex: DoseException[]): Record<Bucket, number> {
    const monthEx = ex.filter(e => ymd(e.date).startsWith(m.month));
    const c = { ...m.counts };
    for (const b of RECORD_BUCKETS) {
        c[b] = monthEx.filter(e => e.bucket === b).length;
    }
    return c;
}

/** GET /api/medications/stats. Board twin omits refused_by_client and missed_by_client. */
export function medicationStats(q: Quarter | null, board = false): Record<string, unknown> {
    const s = scopeOf(q);
    const { months, exceptions } = history('all');
    const scopedMonths = months.filter(m => s.months.includes(m.month));
    const ex = exceptions.filter(e => inScope(e.date, s));

    const by = emptyBuckets();
    const codes: Record<string, number> = {};
    for (const m of scopedMonths) {
        const c = monthCounts(m, ex);
        for (const b of BUCKETS) {
            by[b] += c[b] || 0;
        }
        for (const [code, n] of Object.entries(m.codes)) {
            codes[code] = (codes[code] || 0) + n;
        }
    }
    // The trend chart carries the twelve months up to the scope's end.
    const ts = trendScopeOf(q);
    const trendEx = exceptions.filter(e => inScope(e.date, ts));
    const trend: Record<string, unknown>[] = [];
    for (const m of months.filter(x => ts.months.includes(x.month))) {
        const c = monthCounts(m, trendEx);
        const monthTotal = BUCKETS.reduce((sum, b) => sum + (c[b] || 0), 0);
        if (monthTotal > 0) {
            trend.push({
                month: m.month,
                total: monthTotal,
                given: c.given + c.self_administered,
                refused: c.refused,
                missed: c.not_received + c.medication_issue,
            });
        }
    }
    for (const e of ex) {
        codes[e.code] = (codes[e.code] || 0) + 1;
    }

    const total = BUCKETS.reduce((sum, b) => sum + by[b], 0);
    const exclusions = by.not_required + by.client_unavailable;
    const owed = total - exclusions;
    const given = by.given + by.self_administered;

    const refusedEx = ex.filter(e => e.bucket === 'refused');
    const missedEx = ex.filter(e => e.bucket === 'not_received' || e.bucket === 'medication_issue');

    const out: Record<string, unknown> = {
        total,
        given,
        refused: by.refused,
        missed: by.not_received + by.medication_issue,
        needs_review: by.unclassified,
        excluded: exclusions,
        compliance_rate: owed > 0 ? round1((given / owed) * 100) : 100.0,
        by_bucket: by,
        by_reason: Object.entries(codes)
            .filter(([, n]) => n > 0)
            .sort((a, b) => b[1] - a[1] || Number(a[0]) - Number(b[0]))
            .map(([code, count]) => ({
                code,
                label: REASON_CODES[code].label,
                bucket: REASON_CODES[code].bucket,
                excluded: excluded(REASON_CODES[code].bucket),
                count,
            })),
        refused_by_client: topBy(refusedEx, e => e.client, 'count', 'client'),
        refused_by_medicine: topBy(refusedEx, e => e.medicine, 'count', 'medicine_name'),
        missed_by_client: topBy(missedEx, e => e.client, 'count', 'client'),
        missed_by_medicine: topBy(missedEx, e => e.medicine, 'count', 'medicine_name'),
        monthly_trends: trend.slice(-12),
        last_synced_at: syncedAt('medications'),
    };
    if (board) {
        delete out['refused_by_client'];
        delete out['missed_by_client'];
    }
    return out;
}

/** GET /api/medications/export[?reason=missed|refused|review]. */
export function medicationExport(q: Quarter | null, reason: string | null): Record<string, unknown> {
    const s = scopeOf(q);
    const want = reason && ['missed', 'refused', 'review'].includes(reason) ? reason : 'all';
    const rows = history('all')
        .exceptions.filter(e => inScope(e.date, s))
        .filter(e => want === 'all' || categoryOf(e.bucket) === want)
        .map(e => ({
            client: e.client,
            medicine_name: e.medicine,
            dose: e.dose,
            quantity: e.dose,
            route: e.route,
            record_date: ymd(e.date),
            due_time: e.due_time,
            actual_time: null,
            missed: e.code,
            action: null,
            actioned_by: e.actioned_by,
            reason_code: e.code,
            reason_label: REASON_CODES[e.code].label,
            reason_bucket: e.bucket,
            reason_category: categoryOf(e.bucket),
        }));
    return { quarter: q?.key ?? null, reason: want, count: rows.length, rows };
}
